"use client";
import React, { useState } from "react";
import cn from "@/utils/cn";
import Image from "next/image";
import { useFormContext } from "react-hook-form";
import InputCrossIcon from "@/assets/icons/input-cross-icon.svg";
import { InputErrorIcon } from "@/assets/svg-icons/svg-icons";
import TextComponent from "@/app/_components/TextComponent";

type InputFieldProps = {
  name: string;
  label?: string;
  labelClassName?: string;
  isRequired?: boolean;
  subLabel?: string;
  errorTextPosition?: "right" | "top";
  showErrorInTooltip?: boolean;
  labelStyle?: React.CSSProperties;
  containerClassName?: string;
} & React.InputHTMLAttributes<HTMLInputElement>;

export default function InputField({
  name,
  label,
  className,
  labelClassName = "",
  isRequired = false,
  subLabel = "",
  errorTextPosition = "right",
  showErrorInTooltip = false,
  labelStyle = {},
  containerClassName = "",
  ...props
}: InputFieldProps) {
  const {
    watch,
    register,
    formState: { errors },
    setValue,
  } = useFormContext();

  const [showTooltip, setShowTooltip] = useState(false);

  const error = errors?.[name]?.message;
  const hasLabel = label && label.trim();
  return (
    <div className={cn("w-full", containerClassName)}>
      <div
        className={cn("flex items-center ", {
          "flex-col-reverse items-start": errorTextPosition === "top",
        })}
      >
        {hasLabel && (
          <div className="flex items-center">
            <TextComponent
              type="h4"
              className={cn(
                // "text-kd-secondary-2 font-medium leading-5 xl:text-base text-[10.5px]",
                "font-medium text-kd-secondary-2",
                labelClassName,
              )}
              style={labelStyle}
            >
              {label}
              {!error && (
                <span className="ml-2 text-[9.24px] font-[400] leading-[20px] text-kd-secondary-3 xl:text-[14px]">
                  {subLabel}
                </span>
              )}
            </TextComponent>
          </div>
        )}

        {error && (
          <div
            className={"ml-2 flex items-start text-[#FA5A7D]"}
            style={{
              color: (labelStyle as any)?.requiredColor || "#FA5A7D",
            }}
          >
            <div
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              className="relative -mt-2 flex cursor-pointer items-center"
            >
              <InputErrorIcon className="max-xl:size-1.5" />
              {showErrorInTooltip && showTooltip && (
                <div className="absolute left-1/2 top-full z-10 mt-3 -translate-x-1/2 whitespace-nowrap rounded-lg border border-[#FA5A7D] bg-white p-2 text-xs shadow-lg">
                  {String(error)}
                  <div className="absolute -top-2 left-1/2 size-3 -translate-x-1/2 rotate-45 border-l border-t border-[#FA5A7D] bg-white"></div>
                </div>
              )}
              {!showErrorInTooltip && (
                <p className="ml-1 text-[9px] xl:text-xs">{String(error)}</p>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="relative mt-1">
        <input
          id={name}
          className={cn(
            "w-full bg-[#F6F6F6] rounded-[100px] pl-3 pr-9 md:pb-1.5 md:pt-1.5 py-1 text-[10.5px] xl:text-[14px] text-kd-secondary-1 placeholder:text-kd-secondary-3 font-normal relative focus:outline-none focus:ring-1 focus:ring-kd-lte-blue",
            error && "focus:ring-[#FA5A7D]",
            "h-10 w-full rounded-[10.56px] bg-[#F9FAFB] px-[24px] xl:h-[60px] xl:rounded-2xl",
            className,
          )}
          suppressHydrationWarning={true}
          {...props}
          {...register(name)}
        />

        {watch(name) && !props.disabled && (
          <div
            onClick={() => {
              setValue(name, "");
            }}
            className="absolute inset-y-0 right-[16px] flex h-full cursor-pointer items-center justify-center"
          >
            <Image src={InputCrossIcon} alt="Clear input field" />
          </div>
        )}
      </div>
    </div>
  );
}
